import { useState, useEffect } from 'react';
import { vendorsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Store, Plus, Search, Edit2, Trash2, X, Phone, Mail, MapPin,
  Building2, IndianRupee, ChevronRight, AlertCircle,
} from 'lucide-react';
import { cn } from '../lib/utils';

const VENDOR_CATEGORIES = [
  { value: 'medical_supplies', label: 'Medical Supplies' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'catering', label: 'Catering' },
  { value: 'IT', label: 'IT & Software' },
  { value: 'others', label: 'Others' },
];

const categoryColor = (cat) => {
  const map = {
    medical_supplies: 'bg-blue-100 text-blue-700',
    equipment: 'bg-purple-100 text-purple-700',
    maintenance: 'bg-amber-100 text-amber-700',
    catering: 'bg-orange-100 text-orange-700',
    IT: 'bg-cyan-100 text-cyan-700',
    others: 'bg-slate-100 text-slate-700',
  };
  return map[cat] || map.others;
};

const emptyForm = {
  name: '', company: '', email: '', phone: '', gstin: '', address: '',
  bank_name: '', bank_account: '', bank_ifsc: '', category: 'others', notes: '',
};

export const VendorsPage = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await vendorsAPI.list();
      setVendors(res.data || []);
    } catch { toast.error('Failed to load vendors'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = vendors.filter(v =>
    `${v.name} ${v.company} ${v.category}`.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => { setForm(emptyForm); setEditId(null); setShowForm(true); };
  const openEdit = (v) => {
    setForm({
      name: v.name, company: v.company, email: v.email, phone: v.phone,
      gstin: v.gstin, address: v.address, bank_name: v.bank_name,
      bank_account: v.bank_account, bank_ifsc: v.bank_ifsc,
      category: v.category, notes: v.notes,
    });
    setEditId(v.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      if (editId) {
        await vendorsAPI.update(editId, form);
        toast.success('Vendor updated');
      } else {
        await vendorsAPI.create(form);
        toast.success('Vendor created');
      }
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      await vendorsAPI.delete(id);
      toast.success('Vendor deleted');
      load();
      if (detail?.id === id) setDetail(null);
    } catch { toast.error('Delete failed'); }
  };

  const openDetail = async (v) => {
    try {
      const res = await vendorsAPI.get(v.id);
      setDetail(res.data);
    } catch { toast.error('Failed to load vendor details'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-500">Loading vendors...</div>;

  return (
    <div data-testid="vendors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Vendors</h1>
          <p className="text-sm text-slate-500 mt-1">{vendors.length} vendor{vendors.length !== 1 ? 's' : ''} registered</p>
        </div>
        <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="add-vendor-btn">
          <Plus size={16} /> Add Vendor
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-5 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search vendors..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="vendor-search"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vendor List */}
        <div className={cn("lg:col-span-2", detail && "lg:col-span-1")}>
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-slate-200">
              <Store size={40} className="text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">No vendors found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((v) => (
                <div
                  key={v.id}
                  onClick={() => openDetail(v)}
                  className={cn(
                    "bg-white rounded-lg border border-slate-200 p-4 cursor-pointer hover:shadow-md transition-all duration-200 group",
                    detail?.id === v.id && "ring-2 ring-emerald-500 border-emerald-300"
                  )}
                  data-testid={`vendor-card-${v.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900 truncate">{v.name}</h3>
                        <span className={cn("text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase", categoryColor(v.category))}>
                          {VENDOR_CATEGORIES.find(c => c.value === v.category)?.label || v.category}
                        </span>
                      </div>
                      {v.company && <p className="text-xs text-slate-500">{v.company}</p>}
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        {v.phone && <span className="flex items-center gap-1"><Phone size={11} />{v.phone}</span>}
                        {v.email && <span className="flex items-center gap-1"><Mail size={11} />{v.email}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-right mr-2">
                        <p className="text-xs text-slate-400">Total Purchases</p>
                        <p className="text-sm font-bold text-slate-700">
                          <IndianRupee size={12} className="inline" />{(v.total_purchases || 0).toLocaleString('en-IN')}
                        </p>
                      </div>
                      <ChevronRight size={16} className="text-slate-300 group-hover:text-emerald-500 transition-colors" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Vendor Detail */}
        {detail && (
          <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 p-5" data-testid="vendor-detail">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">{detail.name}</h2>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(detail)} data-testid="edit-vendor-btn">
                  <Edit2 size={14} className="mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => handleDelete(detail.id)} data-testid="delete-vendor-btn">
                  <Trash2 size={14} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setDetail(null)}><X size={14} /></Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm mb-5">
              {detail.company && <div><span className="text-slate-400 block text-xs">Company</span>{detail.company}</div>}
              <div><span className="text-slate-400 block text-xs">Category</span><span className={cn("text-xs px-2 py-0.5 rounded-full font-semibold", categoryColor(detail.category))}>{VENDOR_CATEGORIES.find(c => c.value === detail.category)?.label}</span></div>
              {detail.phone && <div><span className="text-slate-400 block text-xs">Phone</span>{detail.phone}</div>}
              {detail.email && <div><span className="text-slate-400 block text-xs">Email</span>{detail.email}</div>}
              {detail.gstin && <div><span className="text-slate-400 block text-xs">GSTIN</span>{detail.gstin}</div>}
              {detail.address && <div className="col-span-2"><span className="text-slate-400 block text-xs">Address</span>{detail.address}</div>}
            </div>

            {(detail.bank_name || detail.bank_account) && (
              <div className="bg-slate-50 rounded-lg p-3 mb-5">
                <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Bank Details</h4>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  {detail.bank_name && <div><span className="text-slate-400 block text-xs">Bank</span>{detail.bank_name}</div>}
                  {detail.bank_account && <div><span className="text-slate-400 block text-xs">Account</span>{detail.bank_account}</div>}
                  {detail.bank_ifsc && <div><span className="text-slate-400 block text-xs">IFSC</span>{detail.bank_ifsc}</div>}
                </div>
              </div>
            )}

            {/* Financial Summary */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <p className="text-xs text-emerald-600 font-medium">Total</p>
                <p className="text-lg font-bold text-emerald-700"><IndianRupee size={14} className="inline" />{(detail.total_purchases || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-xs text-blue-600 font-medium">Paid</p>
                <p className="text-lg font-bold text-blue-700"><IndianRupee size={14} className="inline" />{(detail.total_paid || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center">
                <p className="text-xs text-amber-600 font-medium">Pending</p>
                <p className="text-lg font-bold text-amber-700"><IndianRupee size={14} className="inline" />{(detail.total_pending || 0).toLocaleString('en-IN')}</p>
              </div>
            </div>

            {/* Purchase History */}
            {detail.purchase_history?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Recent Purchases</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {detail.purchase_history.slice(0, 10).map((exp) => (
                    <div key={exp.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg text-sm">
                      <div>
                        <p className="font-medium text-slate-700">{exp.description || exp.category_name}</p>
                        <p className="text-xs text-slate-400">{exp.date} &middot; {exp.expense_number}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-slate-700"><IndianRupee size={12} className="inline" />{exp.total_amount?.toLocaleString('en-IN')}</p>
                        <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium",
                          exp.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        )}>{exp.payment_status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="vendor-form-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold text-slate-900">{editId ? 'Edit Vendor' : 'New Vendor'}</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Vendor name" data-testid="vendor-name-input" />
                </div>
                <div>
                  <Label>Company</Label>
                  <Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} placeholder="Company name" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="vendor@email.com" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="Phone number" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({...form, category: e.target.value})}
                    className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                    data-testid="vendor-category-select"
                  >
                    {VENDOR_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label>GSTIN</Label>
                  <Input value={form.gstin} onChange={(e) => setForm({...form, gstin: e.target.value})} placeholder="GST Number" />
                </div>
              </div>
              <div>
                <Label>Address</Label>
                <Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} placeholder="Full address" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Bank Name</Label>
                  <Input value={form.bank_name} onChange={(e) => setForm({...form, bank_name: e.target.value})} placeholder="Bank" />
                </div>
                <div>
                  <Label>Account No</Label>
                  <Input value={form.bank_account} onChange={(e) => setForm({...form, bank_account: e.target.value})} placeholder="Account" />
                </div>
                <div>
                  <Label>IFSC</Label>
                  <Input value={form.bank_ifsc} onChange={(e) => setForm({...form, bank_ifsc: e.target.value})} placeholder="IFSC" />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Additional notes" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-vendor-btn">
                {saving ? 'Saving...' : editId ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorsPage;
